import type { ReactNode } from 'react';
import { matrixTone, type Tone } from './state.ts';
import { NO_VALUE } from './format.ts';
import styles from './Distribution.module.css';

/* ==========================================================================
   Distribution — the facet rail. `label / right-aligned count / proportional
   bar` on a 20px pitch, so a set of counts reads as a shape instead of as a
   row of pills.

   THE COLOUR RULE THIS PRIMITIVE EXISTS TO ENFORCE
   -----------------------------------------------
   A distribution bar is length-encoded: the longest bar is the biggest
   category. If every category also gets its own saturated hue, the healthy
   majority ends up wearing the most ink on the screen — twelve nominal robots
   painting a long bright green bar while the two faulted ones get a short red
   one. That is colour proportional to *count*, which is the exact inverse of
   what an operator needs.

   So the bar is `--nominal` steel by default and a state hue is spent only
   where a threshold has actually been crossed. `matrixTone` is the projection
   that decides: fault stays red, held-for-maintenance stays purple, anything
   not current goes amber, no-data stays hollow, everything else is steel. The
   category is still named — by its word and by its 6px dot — so nothing is
   lost but saturation.

   A caller cannot opt out of this, and that is deliberate.
   ========================================================================== */

export interface DistItem {
  key: string;
  /** The category, named. Usually a `<StatusBadge>` — dot plus word. */
  label: ReactNode;
  /** `null` renders as `—`; a category we have no count for is not zero. */
  count: number | null | undefined;
  /** The category's semantic tone. Projected, never painted literally. */
  tone?: Tone | undefined;
  /** Nothing is arriving for this category — the bar draws hatched. */
  hollow?: boolean | undefined;
  title?: string | undefined;
}

export function Distribution({
  items,
  loading = false,
  selected,
  onSelect,
  barWidth,
}: {
  items: readonly DistItem[];
  loading?: boolean | undefined;
  /** The facet currently filtering the view this rail belongs to. */
  selected?: string | null | undefined;
  onSelect?: ((key: string) => void) | undefined;
  /**
   * Cap on the bar column, in px. By default the bar takes the rail's spare
   * width — pass this only where the rail must stay narrow (a 320px detail
   * rail beside a map, say). Bars align across rows either way.
   */
  barWidth?: number | undefined;
}) {
  const peak = Math.max(1, ...items.map((i) => i.count ?? 0));

  return (
    <div
      className={styles.rail}
      style={barWidth === undefined ? undefined : { ['--dist-bar' as string]: `${barWidth}px` }}
    >
      {items.map((item) => {
        const n = item.count ?? null;
        /* Steel unless a human needs to act. See the note above. */
        const paint: Tone = item.tone === undefined ? 'nominal' : matrixTone(item.tone);
        const frac = n === null ? 0 : n / peak;
        const active = selected != null && selected === item.key;

        const body = (
          <>
            <span className={styles.label}>{item.label}</span>
            <span className={styles.count}>{loading ? '…' : n === null ? NO_VALUE : n}</span>
            <span className={styles.track} data-tone={paint} data-hollow={item.hollow === true}>
              {n !== null && n > 0 && (
                <span className={styles.bar} style={{ width: `${frac * 100}%` }} />
              )}
            </span>
          </>
        );

        return onSelect === undefined ? (
          <div key={item.key} className={styles.row} title={item.title}>
            {body}
          </div>
        ) : (
          <button
            key={item.key}
            type="button"
            className={styles.row}
            data-selected={active}
            aria-pressed={active}
            title={item.title}
            onClick={() => onSelect(item.key)}
          >
            {body}
          </button>
        );
      })}
    </div>
  );
}
